import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { HelpCircle, Mail } from 'lucide-react';
import { fadeInUp } from '../lib/animations';

const FAQS = [
  {
    q: 'Is my payment information secure?',
    a: 'Yes. All payments are processed securely through Stripe, a PCI-compliant payment processor used by millions of businesses worldwide. Tune4Tune never sees or stores your card details.',
  },
  {
    q: "What happens if there's a dispute between buyer and artist?",
    a: 'If a dispute arises, our admin team reviews the order details, communication, and submitted dubplate to reach a fair resolution. We recommend clearly detailing your lyrics, riddim, and any special requests at checkout to avoid misunderstandings.',
  },
  {
    q: 'What formats do I receive my dubplate in?',
    a: 'Dubplates are uploaded by the artist. Typically it will be either mp3 or wav format.',
  },
  {
    q: 'Can I use my dubplate commercially?',
    a: 'Dubplates are intended primarily for personal/promotional use. Commercial usage rights may vary by artist. Check with the artist before using a dubplate in advertising, streaming releases, or other commercial projects.',
  },
  {
    q: 'How long does it take to receive my dubplate after ordering?',
    a: 'Timing depends on the artist. They set the turnaround time depending on their schedule.',
  },
  {
    q: 'Can I request a specific riddim or lyrics?',
    a: 'Definitely. You write the lyrics you want when placing the order. You can upload the riddim you want used as well.',
  },
  {
    q: 'How does payment/escrow work? When does the artist actually get paid?',
    a: 'Money is held in an escrow account until the dubplate is sent to the user. After the user downloads the dubplate the money is sent to the artist\u2019s account and can be withdrawn at their leisure.',
  },
  {
    q: 'Can I order from artists based outside Jamaica / the Caribbean?',
    a: 'We aren\u2019t quite there as yet but that is the goal.',
  },
  {
    q: 'What if the artist misses the agreed deadline?',
    a: 'If the artist misses their deadline the money moves from the escrow account and is returned to the user\u2019s method of payment.',
  },
  {
    q: 'How do I get verified or listed on the platform?',
    a: 'Artists are verified through direct contact with our admin team, confirming their identity before being listed. This ensures buyers are always dealing directly with the real artist \u2014 not a middleman or impersonator.',
  },
  {
    q: 'How much commission does Tune4Tune take?',
    a: '10% of all transactions (5% from the user and 5% from the artist).',
  },
];

export default function FAQ() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20" data-testid="faq-page">
        <motion.div {...fadeInUp} transition={{ duration: 0.5 }} className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-lime/10 mb-4">
            <HelpCircle className="w-7 h-7 text-lime" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-3">FAQ</h1>
          <p className="text-[#A1A1AA] max-w-xl mx-auto">
            Everything you wanted to know about ordering dubplates on Tune4Tune.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-list">
            {FAQS.map((item, idx) => (
              <AccordionItem
                key={item.q}
                value={`item-${idx}`}
                className="bg-surface border border-[#262626] rounded-xl px-5 data-[state=open]:border-lime/40 transition-colors"
                data-testid={`faq-item-${idx}`}
              >
                <AccordionTrigger
                  className="font-semibold text-left text-base sm:text-lg hover:no-underline py-5"
                  data-testid={`faq-q-${idx}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  className="text-sm sm:text-base text-[#A1A1AA] pb-5 leading-relaxed"
                  data-testid={`faq-a-${idx}`}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 p-8 rounded-2xl bg-gradient-to-br from-lime/10 to-transparent border border-lime/30 text-center"
          data-testid="faq-still-stuck"
        >
          <h2 className="font-heading text-2xl font-bold mb-2">Still have questions?</h2>
          <p className="text-[#A1A1AA] mb-5">Drop us a line and the team will get back to you.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="mailto:coderedmusicgroup@gmail.com"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-lime text-black font-semibold hover:bg-lime/90 transition-colors"
              data-testid="faq-contact-email"
            >
              <Mail className="w-4 h-4" /> coderedmusicgroup@gmail.com
            </a>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#333] text-white hover:bg-white/5 transition-colors"
              data-testid="faq-about-link"
            >
              Learn more about us
            </Link>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
